import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '~/app/store';
import { useAllowedContexts } from '~/features/fleets/switcher/hooks/useAllowedContexts';
import { transformAllowedContexts } from '~/features/fleets/switcher/utils/fleetSwitcher.utils';
import {
  type ContextItem,
  setSelectedContext,
} from '~/store/profile/profileSlice';
import { selectActingPermissionsContextIds } from '~/store/profile/profileSlice.selectors';
import type { RouteParams } from '../configs/route.config';
import useCurrentContext from './useCurrentContext';
import useFleetSwitcherVisibility from './useFleetSwitcherVisibility';

interface ContextData {
  organizationsId?: string;
  hubsId?: string;
  fleetsId?: string;
}

interface UseContextGuardReturn {
  contextData: ContextData | undefined;
  transformedContexts:
    | { root: ContextItem; subContexts: ContextItem[] }
    | undefined;
  isContextLoading: boolean;
  isContextReady: boolean;
}

/**
 * Hook that provides context data only when it's fully loaded and valid.
 * This prevents API requests from being made with incomplete or undefined context data.
 *
 * The hook handles two scenarios:
 * 1. **Fleet Switcher Enabled & Visible**: Uses currentContext from the fleet switcher state
 * 2. **Fleet Switcher Disabled/Hidden**: Falls back to URL params for context data
 *
 * @returns Object containing:
 *   - `contextData`: The context data if available, undefined if still loading
 *   - `isContextLoading`: Boolean indicating if context is still loading
 *   - `isContextReady`: Boolean indicating if context is ready for API calls
 *
 * @example
 * ```tsx
 * const { contextData, isContextLoading, isContextReady } = useContextGuard();
 *
 * // Wait for context to be ready before making API calls
 * useEffect(() => {
 *   if (isContextReady && contextData.organizationsId) {
 *     fetchOrganizationData(contextData.organizationsId);
 *   }
 * }, [isContextReady, contextData]);
 *
 * if (isContextLoading) {
 *   return <LoadingSpinner />;
 * }
 * ```
 */
const useContextGuard = (): UseContextGuardReturn => {
  const dispatch = useAppDispatch();
  const { allowedContexts, canFetchAllowedContexts } = useAllowedContexts();
  const actingPermissionsContextIds = useSelector(
    selectActingPermissionsContextIds
  );

  const transformedContexts = useMemo(() => {
    return transformAllowedContexts({
      allowedContexts,
      actingPermissionsContextIds,
    });
  }, [allowedContexts, actingPermissionsContextIds]);

  const currentContext = useCurrentContext();
  const params = useParams<RouteParams>();
  const { isFleetSwitcherVisible } = useFleetSwitcherVisibility();

  // Extract specific param values to optimize memo dependencies
  const {
    organizationsId: paramsOrgId,
    hubsId: paramsHubId,
    fleetsId: paramsFleetId,
  } = params;

  // Ensures that the current context is valid and updates it via selectedContext if necessary
  useEffect(() => {
    // Adding to prevent organization context from being set after current context and acting profile are unset
    if (!actingPermissionsContextIds?.organizationsId) {
      return;
    }

    if (!currentContext && transformedContexts?.root) {
      dispatch(setSelectedContext(transformedContexts.root));
      return;
    }

    if (currentContext?.organizationsId && transformedContexts?.root) {
      const matchesRoot =
        currentContext.organizationsId === transformedContexts.root.id;
      if (matchesRoot) return;

      const matchesSubContext = transformedContexts.subContexts.some(
        (subContext) => subContext.id === currentContext.organizationsId
      );
      if (matchesSubContext) return;

      dispatch(setSelectedContext(transformedContexts.root));
    }
  }, [
    actingPermissionsContextIds,
    dispatch,
    currentContext,
    transformedContexts,
  ]);

  const contextData = useMemo((): ContextData | undefined => {
    // Priority 1: Use currentContext if available and has organizationsId
    if (currentContext?.organizationsId) {
      return currentContext;
    }

    // Priority 2: Fall back to URL params if currentContext is not available
    // This handles cases where fleet switcher is disabled or context is intentionally empty
    if (!currentContext && paramsOrgId) {
      return {
        organizationsId: paramsOrgId,
        hubsId: paramsHubId,
        fleetsId: paramsFleetId,
      };
    }

    // Context is not ready yet
    return undefined;
  }, [currentContext, paramsOrgId, paramsHubId, paramsFleetId]);
  const isContextLoading = useMemo(() => {
    // If fleet switcher is enabled and visible and acting permissions context ids has organizations id, we're loading until currentContext is available
    if (isFleetSwitcherVisible && canFetchAllowedContexts) {
      return !currentContext;
    }

    // If fleet switcher is disabled/hidden, we're loading until we have organizationsId from either source
    return !contextData?.organizationsId;
  }, [
    isFleetSwitcherVisible,
    currentContext,
    contextData,
    canFetchAllowedContexts,
  ]);

  const isContextReady = useMemo(() => {
    return !isContextLoading && !!contextData?.organizationsId;
  }, [isContextLoading, contextData]);

  return {
    contextData,
    transformedContexts,
    isContextLoading,
    isContextReady,
  };
};

export default useContextGuard;
